import { useState } from "react";
import { admin } from "../../api/client";
import type { AccessRequest, Lead } from "../../api/types";
import { can, staffRole, useMe } from "../../auth/session";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Pill, Spinner, useToast } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateTimeShort, formatCNPJ } from "../../lib/format";
import { DecisionModal } from "./shared";

const STATUSES = ["pending", "approved", "rejected"] as const;

/** Beta fechado: fila de cadastros esperando liberação do admin. */
export function TocaAcessos() {
  const me = useMe();
  const toast = useToast();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("pending");
  const { data, error, loading, reload } = useAsync(() => admin.users(status), [status]);
  const [deciding, setDeciding] = useState<AccessRequest | null>(null);
  const allowed = can.approveAccess(staffRole(me));
  const leads = useAsync(() => admin.leads(), []);
  const canContact = can.actOnOrders(staffRole(me));

  return (
    <TocaPage title={t.toca.accessTitle}>
      <p className="ink2" style={{ maxWidth: "60ch" }}>{t.toca.accessLead}</p>
      <div className="chips" role="tablist">
        {STATUSES.map((s) => (
          <button key={s} type="button" className="chip" role="tab" aria-selected={status === s} onClick={() => setStatus(s)}>
            {t.toca.accessStatus[s]}
          </button>
        ))}
      </div>
      <div className="card">
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : !data || data.length === 0 ? (
          <Empty body={t.toca.accessEmpty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.toca.accessColWho}</th>
                  <th>{t.toca.accessColCompany}</th>
                  <th>{t.toca.accessColWhen}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="cell2">
                        <b>{u.name}</b>
                        <span className="mono">{u.email}</span>
                      </div>
                    </td>
                    <td>
                      {u.company ? (
                        <div className="cell2">
                          <span>{u.company.legalName}</span>
                          <span className="mono">{formatCNPJ(u.company.cnpj)}</span>
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(u.createdAt)}</td>
                    <td className="r">
                      {allowed ? (
                        <button type="button" className="btn dark xs" onClick={() => setDeciding(u)}>{t.toca.accessDecide}</button>
                      ) : u.accessNote ? (
                        <span className="muted small">{u.accessNote}</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!allowed && <span className="muted small">{t.common.readonlyHint}</span>}

      <div className="card">
        <div className="card-head">
          <h2>{t.toca.leadsTitle}</h2>
          <Pill tone="mute">{leads.data?.length ?? 0}</Pill>
        </div>
        <p className="ink2" style={{ padding: "0 16px 12px", maxWidth: "60ch" }}>{t.toca.leadsLead}</p>
        {leads.loading && !leads.data ? (
          <Spinner />
        ) : leads.error ? (
          <div style={{ padding: 16 }}><ErrorBox error={leads.error} onRetry={leads.reload} /></div>
        ) : !leads.data || leads.data.length === 0 ? (
          <Empty body={t.toca.leadsEmpty} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.auth.email}</th>
                  <th>{t.toca.leadsColCnpj}</th>
                  <th>{t.toca.leadsColWhen}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {leads.data.map((l: Lead) => (
                  <tr key={l.id}>
                    <td className="mono">{l.email}</td>
                    <td>
                      <div className="cell2">
                        <span className="mono">{formatCNPJ(l.cnpj)}</span>
                        {l.answers && <span className="muted small">{Object.entries(l.answers).filter(([, v]) => v).map(([k, v]) => `${t.toca.leadsAnswers[k] ?? k}: ${v}`).join(" · ")}</span>}
                      </div>
                    </td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(l.createdAt)}</td>
                    <td className="r">
                      {l.contactedAt ? (
                        <Pill tone="ok">{t.toca.leadsContacted}</Pill>
                      ) : canContact ? (
                        <button type="button" className="btn ghost xs" onClick={async () => { await admin.leadContacted(l.id, ""); toast(t.toca.leadsMarked); void leads.reload(); }}>{t.toca.leadsMarkContacted}</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {deciding && (
        <DecisionModal
          title={t.toca.accessDecide}
          subject={`${deciding.name} · ${deciding.email}`}
          options={[
            { value: "approved" as const, label: t.toca.accessDecision.approved! },
            { value: "rejected" as const, label: t.toca.accessDecision.rejected!, danger: true },
          ]}
          onDecide={async (d, note) => {
            await admin.userAccess(deciding.id, d, note);
            toast(fmt(t.toca.accessDecided, { d: d === "approved" ? "liberado" : "rejeitado" }));
            void reload();
          }}
          onClose={() => setDeciding(null)}
        />
      )}
    </TocaPage>
  );
}
